import { supabase } from "@/integrations/supabase/client";

export type HandoffTrigger = "ai_tool" | "button";
export type HandoffStageOutcome = "success" | "fail";

interface HandoffAttemptContext {
  agentId: string;
  /** ElevenLabs conversation the handoff started from; null when no AI session is running. */
  conversationId: string | null;
  trigger: HandoffTrigger;
}

/**
 * Records every stage of one human-handoff attempt against the conversation
 * log, so a call that never reaches Twilio still leaves a trail of how far the
 * browser got. Entries are fire-and-forget: logging must never delay or break
 * the handoff itself.
 *
 * The conversation id is captured up front because the later stages (call
 * accepted, call ended) happen after the AI session — and its id — are gone.
 */
export class HandoffAttemptLog {
  public readonly attemptId = crypto.randomUUID();
  private readonly startedAt = Date.now();
  private sequence = 0;

  constructor(private readonly context: HandoffAttemptContext) {}

  public get conversationId(): string | null {
    return this.context.conversationId;
  }

  public record(stage: string, outcome: HandoffStageOutcome = "success", details: Record<string, unknown> = {}): void {
    this.sequence += 1;

    supabase.functions
      .invoke("logger", {
        body: {
          action: "conversation",
          data: {
            // An attempt made with no AI session still needs a log to hang off.
            el_conv_id: this.context.conversationId ?? `handoff_${this.attemptId}`,
            agent_id: this.context.agentId,
            action: `handoff:${stage}`,
            status: outcome,
            payload: JSON.stringify({
              attempt_id: this.attemptId,
              trigger: this.context.trigger,
              seq: this.sequence,
              elapsed_ms: Date.now() - this.startedAt,
              ...details,
            }),
          },
        },
      })
      .catch((error) => {
        console.warn("Failed to log handoff stage:", stage, error);
      });
  }
}

/** Twilio errors carry a numeric code and causes that a plain message loses. */
export function describeHandoffError(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== "object") return { message: String(error) };

  const { name, message, code, causes, description, explanation } = error as Record<string, unknown>;
  return {
    name,
    message,
    ...(code !== undefined && { code }),
    ...(description !== undefined && { description }),
    ...(explanation !== undefined && { explanation }),
    ...(Array.isArray(causes) && causes.length > 0 && { causes }),
  };
}
